/**
 * Score of the Week admin card — configurable rotation, manual pin.
 */
import React, { useEffect, useState } from "react";
import { Save, RotateCw, Flame, Clock } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

interface Config {
  enabled: boolean;
  rotation_days: number;
  pinned_product_id: string | null;
  auto_selected_product_id: string | null;
  auto_selected_at: string | null;
  current_product?: {
    product_id: string;
    title: string;
    brand: string | null;
    product_score: number | null;
  } | null;
  next_rotation_at?: string | null;
  days_until_rotation?: number | null;
}

export const ScoreOfWeekCard: React.FC = () => {
  const [cfg, setCfg] = useState<Config | null>(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get<Config>("/admin/score-of-week");
      setCfg(res.data);
    } catch (e: any) {
      toast.error("Failed to load Score of the Week config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const res = await api.put<Config>("/admin/score-of-week", {
        enabled: cfg.enabled,
        rotation_days: cfg.rotation_days,
        pinned_product_id: cfg.pinned_product_id?.trim() || null,
        auto_selected_product_id: cfg.auto_selected_product_id,
        auto_selected_at: cfg.auto_selected_at,
      });
      setCfg(res.data);
      toast.success("Score of the Week updated");
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const forceRotate = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      // Trigger a fresh auto-pick by clearing the auto_selected_at
      const res = await api.put<Config>("/admin/score-of-week", {
        enabled: cfg.enabled,
        rotation_days: cfg.rotation_days,
        pinned_product_id: cfg.pinned_product_id?.trim() || null,
        auto_selected_product_id: null,
        auto_selected_at: null,
      });
      setCfg(res.data);
      toast.success("Rotated — new top product picked");
    } catch (e: any) {
      toast.error("Could not rotate now");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-6 text-sm text-slate-500" data-testid="sow-admin-card-loading">
        Loading Score of the Week config…
      </div>
    );
  }

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="sow-admin-card">
      <div className="flex items-start justify-between gap-4 mb-5">
        <div className="min-w-0">
          <div className="text-[11px] uppercase tracking-widest text-orange-500 font-bold mb-1 flex items-center gap-1.5">
            <Flame className="h-3 w-3" strokeWidth={2.5} />
            Score of the Week
          </div>
          <h2 className="font-serif-display font-bold text-xl text-slate-900 tracking-tight">
            Homepage Featured Product
          </h2>
          <p className="text-xs text-slate-500 mt-1 leading-relaxed">
            Auto-rotates to the highest-scored product every N days. Pin a specific product for manual control.
          </p>
        </div>
        <label className="inline-flex items-center gap-2 shrink-0">
          <input
            type="checkbox"
            checked={cfg.enabled}
            onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
            data-testid="sow-admin-toggle"
            className="h-4 w-4 accent-orange-500"
          />
          <span className="text-sm font-medium text-slate-700">Enabled</span>
        </label>
      </div>

      {cfg.current_product && (
        <div className="mb-5 rounded-xl border border-orange-200 bg-orange-50 p-4">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="text-[10px] uppercase tracking-widest text-orange-500 font-bold">Currently featured</div>
            {cfg.pinned_product_id ? (
              <div
                className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-slate-500"
                data-testid="sow-admin-countdown"
              >
                <Clock className="h-3 w-3" strokeWidth={2.5} />
                Manually pinned
              </div>
            ) : cfg.days_until_rotation != null ? (
              <div
                className="inline-flex items-center gap-1 rounded-full bg-white border border-orange-200 px-2.5 py-0.5 text-[10px] uppercase tracking-wider font-bold text-orange-600"
                title={cfg.next_rotation_at ? `Next rotation on ${new Date(cfg.next_rotation_at).toLocaleString()}` : undefined}
                data-testid="sow-admin-countdown"
              >
                <Clock className="h-3 w-3" strokeWidth={2.5} />
                {cfg.days_until_rotation === 0
                  ? "Rotates on next visit"
                  : cfg.days_until_rotation === 1
                  ? "Next rotation in 1 day"
                  : `Next rotation in ${cfg.days_until_rotation} days`}
              </div>
            ) : null}
          </div>
          <div className="mt-1 flex items-baseline justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold text-slate-900 truncate" data-testid="sow-admin-current-title">{cfg.current_product.title}</div>
              <div className="text-xs text-slate-500">{cfg.current_product.brand || "Unbranded"}</div>
            </div>
            <div className="font-serif-display font-bold text-2xl text-slate-900 tabular-nums shrink-0">
              {cfg.current_product.product_score ?? "—"}
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Rotation window (days)</div>
          <input
            type="number"
            min={1}
            max={90}
            value={cfg.rotation_days}
            onChange={(e) => setCfg({ ...cfg, rotation_days: Math.max(1, Math.min(90, parseInt(e.target.value || "7"))) })}
            data-testid="sow-admin-rotation-days"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm focus:border-orange-400 focus:outline-none"
          />
          <div className="text-[10px] text-slate-400 mt-1">1-90 days. Default 7.</div>
        </label>
        <label className="block">
          <div className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold mb-1.5">Pin a specific product (optional)</div>
          <input
            type="text"
            value={cfg.pinned_product_id || ""}
            onChange={(e) => setCfg({ ...cfg, pinned_product_id: e.target.value })}
            placeholder="product_id (leave blank for auto)"
            data-testid="sow-admin-pinned-id"
            className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono focus:border-orange-400 focus:outline-none"
          />
          <div className="text-[10px] text-slate-400 mt-1">Overrides auto-rotation when set.</div>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={save}
          disabled={saving}
          data-testid="sow-admin-save"
          className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold px-5 py-2 disabled:opacity-50 transition-colors"
        >
          <Save className="h-3.5 w-3.5" strokeWidth={2.5} />
          {saving ? "Saving…" : "Save changes"}
        </button>
        <button
          onClick={forceRotate}
          disabled={saving}
          data-testid="sow-admin-rotate-now"
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 hover:border-slate-900 text-slate-900 text-sm font-semibold px-5 py-2 disabled:opacity-50 transition-colors"
        >
          <RotateCw className="h-3.5 w-3.5" strokeWidth={2.5} />
          Rotate now
        </button>
      </div>
    </div>
  );
};

export default ScoreOfWeekCard;
